<script setup lang="ts">
const isScrollingUp = ref(false)
const scrollPosition = ref(0)
const isShowingSearchBar = ref(false)

const header = ref(null)
const MOBILE_HEADER_OFFSET_TOP = 20
const isScrolledPastHeader = computed((): boolean => {
  return scrollPosition.value > MOBILE_HEADER_OFFSET_TOP
})

const handleScroll = throttle(() => {
  const currentScrollPosition = window.scrollY
  isScrollingUp.value = currentScrollPosition <= scrollPosition.value
  scrollPosition.value = currentScrollPosition
}, 300)

function handleSearchBarVisibility(isShowing: boolean): void {
  isShowingSearchBar.value = isShowing
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll, isPassiveSupported ? { passive: true } : false)
})
onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<template>
  <header
    ref="header"
    class="main-header"
    :class="{
      '--is-scrolled-past-header': isScrolledPastHeader,
      '--is-scrolling-up': isScrolledPastHeader && isScrollingUp,
      '--is-scrolling-down': isScrolledPastHeader && !isScrollingUp,
      '--is-showing-search-bar': isShowingSearchBar,
    }"
  >
    <NuxtLink
      v-show="!isShowingSearchBar"
      to="/"
    >
      <span class="sr-only">Sendcloud homepage</span>
      <img
        class="main-header__logo"
        alt=""
        width="166"
        height="48"
        src="~/assets/images/sendcloud-logo.svg"
      >
    </NuxtLink>

    <div class="main-header__utilities">
      <UiSearchBar
        @search-bar-visibility-changed="handleSearchBarVisibility"
      />
      <UiUserProfileMenu
        :should-close-mobile-menu="isScrolledPastHeader && !isScrollingUp"
      />
    </div>
  </header>
</template>

<style lang="scss" scoped>
@use '@/assets/styles/helpers' as *;

$mobile-header-height-with-extra-margin: 100px;

.main-header {
  display: flex;
  justify-content: space-between;
  max-width: var(--app-layout-default-max-width);
  margin: 0 auto;
  padding: var(--app-spacer-24) var(--app-layout-default-page-padding);

  // separating `::after` from its styles within `max-width` due to technical limitations of `@extend`
  &::after {
    @extend %stretch-absolute;
  }

  @include max-width('md') {
    margin-bottom: var(--app-spacer-16);
    padding-top: var(--app-spacer-12);
    padding-bottom: var(--app-spacer-12);
    transition: transform 0.2s ease-in-out;
    transform: translateY(0);
    position: sticky;
    z-index: 1;
    top: 0;
    background-color: var(--app-background-color);

    &::after {
      content: "";
      z-index: -1;
      box-shadow: var(--app-panel-shadow-sm);
      opacity: 0;
      transition: opacity 0.5s ease-in-out;
    }

    &.--is-scrolled-past-header {
      &::after {
        opacity: 1;
      }
    }

    &.--is-scrolling-up {
      transform: translateY(0);
    }

    &.--is-scrolling-down {
      transform: translateY(-($mobile-header-height-with-extra-margin));
    }

    &.--is-showing-search-bar {
      background: transparent;
    }
  }
}

.main-header__logo {
  @include max-width('lg') {
    width: 152px;
    height: 44px;
  }
}

.main-header__utilities {
  display: inline-flex;
  margin-left: auto;
  align-items: center;
  gap: var(--app-spacer-8);
}
</style>
